import type { IBookPreview } from '@shared/jp/data/products';
import {
  INPUT_NAME_KUN_CHAN,
  INPUT_VALUE_KUN_CHAN_NASHI,
  kunChanArr,
  KUNCHAN_VALUE_TO_LABEL_MAP,
  INPUT_VALUE_FKMK_COVER_COLOR_MULTICOLOR,
  BOOK_PRICE_FROEBELKAN_MAINICHI_KINENBI,
  BOOK_PRICE_FROEBELKAN_MAINICHI_KINENBI_HARD_COVER,
  FKMK_COVER_COLOR_VALUE_TO_LABEL_MAP,
  fkmkCoverArr,
  INPUT_NAME_FKMK_COVER_COLOR,
} from '@shared/jp/product';
import {
  INPUT_VALUE_COVER_TYPE_HARD,
  INPUT_VALUE_COVER_TYPE_SOFT,
  INPUT_NAME_KIDS_NAME,
  INPUT_NAME_PHOTO,
} from '@shared/common/product';
import { EBannerTextColor } from '@shared/common/data/products';
import { getPriceData } from '@shared/jp/data/products';
import {
  getSelectInputOptionData,
  getSelectInputData,
  getTextInputData,
  getPhotoInputData,
} from '@shared/common/data/inputs';
import {
  getNameValidatorList,
  GET_BOOK_NAME_ERROR,
} from '@shared/jp/data/inputs';
import MainichiKinenbiShared from '@shared/jp/vendors/froebelkan/data/products/books/MainichiKinenbiShared';

import { getCoverHard, getCoverSoft } from './_utils';

export const MainichiKinenbiInputsKidsName = getTextInputData({
  label: '主人公の名前',
  placeholder: 'ひらがな9文字まで',
  name: INPUT_NAME_KIDS_NAME,
  validator: getNameValidatorList(9, 2),
  error: GET_BOOK_NAME_ERROR(9),
} as const);

export const MainichiKinenbiInputsKunChan = getSelectInputData({
  label: 'くん・ちゃん付け',
  placeholder: '選んでください',
  options: kunChanArr.map((value) =>
    getSelectInputOptionData({
      label: KUNCHAN_VALUE_TO_LABEL_MAP[value],
      value,
    }),
  ),
  value: INPUT_VALUE_KUN_CHAN_NASHI,
  name: INPUT_NAME_KUN_CHAN,
} as const);

export const MainichiKinenbiInputsCoverColor = getSelectInputData({
  label: '表紙の色',
  placeholder: '選択してください',
  options: fkmkCoverArr.map((value) =>
    getSelectInputOptionData({
      label: FKMK_COVER_COLOR_VALUE_TO_LABEL_MAP[value],
      value,
    }),
  ),
  value: INPUT_VALUE_FKMK_COVER_COLOR_MULTICOLOR,
  name: INPUT_NAME_FKMK_COVER_COLOR,
} as const);

export const MainichiKinenbiInputsPhoto = getPhotoInputData({
  label: `表紙に入れる写真を選んでください`,
  uploadButtonText: '写真をアップロード',
  okButtonText: '試し読みをする',
  resetButtonText: '写真を選び直す',
  name: INPUT_NAME_PHOTO,
  error: {
    minSize: 'min size is 100KB',
    maxSize: 'max size is 20MB',
  },
  maskUrl: "'assets/images/mainichi-kinenbi/tab9/frame00.png'",
  photoInstructions: '写真を調整してください',
  photoInformation: '※試し読み後、購入に進めます',
});

const prices = {
  [INPUT_VALUE_COVER_TYPE_SOFT]: getPriceData(
    BOOK_PRICE_FROEBELKAN_MAINICHI_KINENBI,
  ),
  [INPUT_VALUE_COVER_TYPE_HARD]: getPriceData(
    BOOK_PRICE_FROEBELKAN_MAINICHI_KINENBI_HARD_COVER,
  ),
} as const;

const coverSoft = getCoverSoft({
  price: prices[INPUT_VALUE_COVER_TYPE_SOFT],
  carousel: [
    'assets/images/covers/mainichikinenbi/SC01.jpg',
    'assets/images/covers/mainichikinenbi/SC02.jpg',
    'assets/images/covers/mainichikinenbi/SC03.jpg',
  ],
});
const coverHard = getCoverHard({
  price: prices[INPUT_VALUE_COVER_TYPE_HARD],
  carousel: [
    'assets/images/covers/mainichikinenbi/HC01.jpg',
    'assets/images/covers/mainichikinenbi/HC02.jpg',
    'assets/images/covers/mainichikinenbi/HC03.jpg',
  ],
});

const MainichiKinenbi: IBookPreview = {
  ...MainichiKinenbiShared,
  caption: 'まいにちきねんび',
  summary:
    '写真とフレームを選んで、日常に魔法をかけちゃおう！世界に1つだけの記念日をひろい集めて、思い出に残せる絵本です。',
  prices: [
    [INPUT_VALUE_COVER_TYPE_SOFT, prices[INPUT_VALUE_COVER_TYPE_SOFT]],
    [INPUT_VALUE_COVER_TYPE_HARD, prices[INPUT_VALUE_COVER_TYPE_HARD]],
  ],
  photos: [
    'assets/images/carouselMK-01.jpg',
    'assets/images/carouselMK-02.jpg',
    'assets/images/carouselMK-03.jpg',
    'assets/images/carouselMK-04.jpg',
    'assets/images/carouselMK-05.jpg',
  ],
  menuPhoto: 'assets/images/BK3-sample.jpg',
  previewPhoto: 'assets/images/BK3-sample.jpg',
  // TODO
  properties: {
    purpose: [],
    age: {
      start: 0,
      end: 3,
    },
  },
  notice: {
    caption:
      'まいまい にちにち くるくる ぱっ！<br>魔法のフレームで、まいにちをかけがえのない記念日に。',
    description:
      'スマホやカメラに撮りためた、何気ない日常の写真。魔法のフレームで縁取って、まいにちを記念日に変えちゃおう！世界に1つだけの記念日をひろい集めて、思い出に残せる絵本です。',
    points: [
      {
        imgSrc: 'assets/images/welcome-notice-point-mainichi-kinenbi-age.png',
        caption: '0歳からおとなまで',
      },
      {
        imgSrc: 'assets/images/welcome-notice-point-mainichi-kinenbi-scene.png',
        caption: '特別な記念日の記録に',
      },
    ],
  },
  points: [
    {
      caption: 'どんなカスタムができるの？',
      description:
        '・主人公(ファミリーネームも可)の名前を入れられる<br>・表紙に入る写真を選べる<br>・記念日の写真を入れられる(最大19枚)<br>・それぞれの写真に関する記録を入れられる<br>・魔法のフレームのデザインを選べる(10パターン)<br>・魔法のフレームの形状を選べる(4パターン)<br>・絵本の制作者を入力できる',
    },
    {
      caption: '絵本のストーリーは？',
      description:
        'スマホやカメラに撮りためた、何気ない日常の写真。<br>魔法のフレームで縁取って、まいにちを記念日に<br>変えちゃおう！<br>世界に1つだけの記念日をひろい集めて、<br>思い出に残せる絵本です。',
    },
    {
      caption: 'ページ数や絵本の大きさは？',
      description:
        '・全24ページ<br>・21cm×21cm(本文部分)の正方形<br>・日本国内で製造</b>',
    },
  ],
  inputs: [
    MainichiKinenbiInputsKidsName,
    MainichiKinenbiInputsKunChan,
    MainichiKinenbiInputsCoverColor,
    MainichiKinenbiInputsPhoto,
  ],
  banner: {
    imgSrc: 'assets/images/bk3-banner-desktop-JP.jpg',
    imgSrcLtMd: 'assets/images/bk3-banner-mobile-JP.jpg',
    text: 'TODO',
    textColor: EBannerTextColor.dark,
  },
  description: [],
  review: {
    text: [
      '写真とフレームを選んで、日常に魔法をかけちゃおう！世界に 1 つだけの記念日をひろい集めて、思い出に残せる絵本です。',
    ],
  },
  reviews: [],
  options: {
    [INPUT_VALUE_COVER_TYPE_HARD]: coverHard,
    [INPUT_VALUE_COVER_TYPE_SOFT]: coverSoft,
  },
  option: INPUT_VALUE_COVER_TYPE_SOFT,
  popularity: 15,
  createdAt: new Date('2020-01-03'),
};

export default MainichiKinenbi;
